import { effect, inject, Injectable, Injector, linkedSignal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { PusherUserEvent } from '../../shared/models/enums/pusher.enums';

import { totalUnreadFeedbackAnswersSchema } from '../../shared/models/schemas/feedback-answer.schema';
import { apiQuery } from '../api/api-query';
import * as AuthSelectors from '../state/auth-state/auth.selectors';
import { HttpFeedbackService } from './http/http-feedback.service';
import { PusherUserChannelService } from './pusher-user-channel.service';
import { PusherService } from './pusher.service';

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  private injector = inject(Injector);
  private store = inject(Store);
  private httpFeedbackService = inject(HttpFeedbackService);
  private pusherService = inject(PusherService);
  private pusherUserChannelService = inject(PusherUserChannelService);

  private isLogged = toSignal(this.store.select(AuthSelectors.isLogged));

  private totalUnreadQuery = apiQuery(
    () => this.httpFeedbackService.countTotalUnreadFeedbackAnswers(),
    { onError: (apiError) => console.error('compteur de réponses de feedback non lues', apiError) }
  );

  readonly totalUnreadFeedbackAnswers = linkedSignal(() => this.totalUnreadQuery.value() ?? 0);

  private lastPushedTotalUnread: number | null = null;
  private lastPushedAt = 0;

  init() {
    this.pusherUserChannelService.bind(
      PusherUserEvent.FEEDBACK_ANSWER_COUNT_UPDATED,
      totalUnreadFeedbackAnswersSchema,
      (totalUnread) => {
        this.lastPushedTotalUnread = totalUnread;
        this.lastPushedAt = Date.now();
        this.totalUnreadFeedbackAnswers.set(totalUnread);
      }
    );

    this.pusherService.onResynchronize(() => {
      if (this.isLogged()) {
        this.updateFeedbackAnswersUnread();
      }
    });

    effect(
      () => {
        if (this.isLogged()) {
          untracked(() => this.updateFeedbackAnswersUnread());
        } else {
          this.totalUnreadFeedbackAnswers.set(0);
        }
      },
      { injector: this.injector }
    );
  }

  async updateFeedbackAnswersUnread() {
    const startedAt = Date.now();
    const totalUnread = await this.totalUnreadQuery.load();
    if (totalUnread === null) return;

    // un event reçu pendant la requête porte une valeur plus récente que la réponse REST : la
    // reposer, car le `load()` a changé la source du linkedSignal, qui l'avait donc abandonnée
    if (this.lastPushedAt > startedAt) {
      this.totalUnreadFeedbackAnswers.set(this.lastPushedTotalUnread!);
      return;
    }

    // `set` explicite : si la réponse vaut le même nombre qu'avant la déconnexion, la source du
    // linkedSignal ne change pas, il ne recalcule pas, et on resterait sur le 0 posé au logout
    this.totalUnreadFeedbackAnswers.set(totalUnread);
  }
}
